import { useCallback, useMemo, useState } from 'react';
import CustomError, { CustomErrorProps } from '../../../common/components/CustomError';
import { useLoading } from '../../../loading/LoadingContext';
import { UserData } from '../../../user/UserContext';
import { OrdersWootTableRow, ColorSetting } from '../../WootOrdersImport.interfaces';
import { Accordion, Badge, ProgressBar, Button, Form } from 'react-bootstrap';
import { Exclamation, Check } from 'react-bootstrap-icons';
import WootFbaJourneyStep from './WootFbaJourneyStep';
import WootFbaJourneyInvoicePreview from './WootFbaJourneyInvoicePreview';
import { generateWootFbaEmailBody } from '../../woot.fba.helpers';
import { EMAIL_SERVICE_HTML_PATH, EmailsTableRow } from '../../../common/common.email.service';
import { apiFetch } from '../../../utility/fetch';
import { DateTime } from 'luxon';
import { notify } from '../../../notify/notify';

interface WootFbaJourneyInvoiceProps {
  row: OrdersWootTableRow
  invoiceColors: ColorSetting
  handleDownloadFile: any
  handlePreviewFile: any
  userData: UserData
  setLastRefresh: any
}

export default function WootFbaJourneyInvoice(props: WootFbaJourneyInvoiceProps) {
  const {changeStep, changeSubStep, toggleLoading} = useLoading()
  const [invoiceError, setInvoiceError]: [CustomErrorProps, any] = useState(undefined)
  const [sendTo, setSendTo] = useState('accounting@nethealthshops.com')

  const handleSendInvoiceEmail = useCallback(async (row: OrdersWootTableRow) => {
    setInvoiceError({visible: false})

    changeStep('PREPARING EMAIL')
    toggleLoading(true)
    
    changeSubStep('Generating Email Body')
    const body = generateWootFbaEmailBody(row)
    
    changeSubStep('Saving Email Body')
    const bodyPath = `${EMAIL_SERVICE_HTML_PATH}/crystal-ball-woot-fba-${row.poNumber}.html`
    const saveHtmlResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: body,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: bodyPath}
      ]
    })
    if(!saveHtmlResponse.ok){
      setInvoiceError({
        content: JSON.stringify(saveHtmlResponse.body),
        description: `Failed to save the email body.`,
        title: 'INVOICE REQUEST ERROR',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    const emailRow: EmailsTableRow = {
      partitionKey: 'crystal-ball',
      rowKey: `woot-fba-invoice-request-${row.poNumber}`,
      attachmentBinaryBlobNames: JSON.stringify([
        row.sourcePoPdfPath,
        row.sourcePoExcelPath,
        row.sourceWrpoPdfPath,
        row.sourceWrpoExcelPath
      ]),
      bodyBlobName: bodyPath,
      status: 'pending',
      subject: `WOOT! FBA - Return PO Invoice Request ${DateTime.now().toLocaleString(DateTime.DATE_SHORT)} - ${row.poNumber} / ${row.wrpoNumber}`,
      sendToAddresses: JSON.stringify(sendTo.split(';'))
    }

    const emailResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'emails',
        records: [emailRow]
      })
    })
    if(!emailResponse.ok){
      setInvoiceError({
        content: JSON.stringify(emailResponse.body),
        description: `Failed to queue up email.`,
        title: 'INVOICE REQUEST ERROR',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    const journeyResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST', 
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [{
          partitionKey: row.partitionKey,
          rowKey: row.rowKey,
          invoiceEmailRow: JSON.stringify(emailRow),
          invoiceStatus: 'complete',
          status: 'complete',
          invoiceTimestamp: DateTime.utc().toISO()
        } as OrdersWootTableRow]
      })
    })
    if(!journeyResponse.ok){
      setInvoiceError({
        content: JSON.stringify(journeyResponse.body),
        description: `Failed to save journey.`,
        title: 'INVOICE REQUEST ERROR',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    props.setLastRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
    notify('success', 'Successfully queued up email to send to accounting!')

    toggleLoading(false)
  }, [sendTo])

  const handleCloseInvoiceError = useCallback(async () => {
    setInvoiceError({visible: false})
  }, [])

  // #region VIEW
  const view = useMemo(() => {
    const {row, invoiceColors} = props  
    const steps = []

    let complete = 0
    let pending = 0

    if(!row.invoiceEmailRow){
      steps.push('SEND INVOICE EMAIL')
      pending += 1
    } else {
      complete += 1
    }

    const stepsMax = 1
    const completeProgress = (complete / stepsMax) * 100

    let warning = 0
    if(completeProgress < 100){
      warning = 1
      pending -= 1
    }

    const pendingProgress = (pending / stepsMax) * 100
    const warningProgress = (warning / stepsMax) * 100
    return <div>
    <Accordion defaultActiveKey={['pending', 'error'].includes(row.invoiceStatus) ? 'invoice' : null} className='custom-accordion'>
      <Accordion.Item eventKey='invoice'>
        <Accordion.Header className='sticky-row-2'>
          <span className='beacon-header'>INVOICE REQUEST EMAIL</span>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <span><Badge bg={invoiceColors.bg} text={invoiceColors.fg}>{row.invoiceStatus?.toUpperCase() ?? 'NOT STARTED'}</Badge></span>
        </Accordion.Header>
        <Accordion.Body>
          <div>
            <div style={{textAlign: 'center'}}>
              <span className='beacon-label'>Steps {complete}/{stepsMax} Complete</span>
              <ProgressBar>
                <ProgressBar animated variant='success' now={completeProgress}/>
                <ProgressBar animated variant='warning' now={warningProgress} />
                <ProgressBar animated variant='secondary' now={pendingProgress} />
              </ProgressBar>
            </div>
            <div>
              <span className='beacon-header'>SEND TO: </span><span className='beacon-label'>SEPARATE WITH A SEMI-COLON</span>
              <Form.Control type='text' value={sendTo} onChange={(e) => setSendTo(e.currentTarget.value)} />
            </div>
            <hr/>
            <div>
            {steps.length > 0 && 
                <div className='beacon-header'>NEXT STEP {
                  steps[0] === 'SEND INVOICE EMAIL' ? <Button variant='success' onClick={async () => {await handleSendInvoiceEmail(row)}} title='Sends the invoice email to accounting.'>{steps[0]}</Button> : null
                }</div>
              }
            </div>
            <div>
              <span className='beacon-header'>REQUIREMENTS</span>
              <div style={{display: 'flex', flexWrap: 'wrap'}}>
                {/* ALLOCATION ITEMS */}
                <WootFbaJourneyStep
                  title='ALLOCATION PDF'
                  item={row.sourcePoPdfPath}
                  itemRequiresParse={false}
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={false}
                  isSuccess={typeof row.sourcePoPdfPath === 'string'}
                ></WootFbaJourneyStep>
                <WootFbaJourneyStep
                  title='ALLOCATION EXCEL'
                  item={row.sourcePoExcelPath}
                  itemRequiresParse={false}
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={false}
                  isSuccess={typeof row.sourcePoExcelPath === 'string'}
                ></WootFbaJourneyStep>
                <WootFbaJourneyStep 
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={true}
                  item={row.allocationIagResponsePath}
                  isSuccess={typeof row.allocationIagResponsePath === 'string'}
                  itemRequiresParse={false}
                  title='CREATED ALLOCATION IA GROUPS'
                />
                {/* RETURN PO ITEMS */}
                {!row.wrpoNumber && <div className='journey-step warning'>
                  <div className='journey-icon'>
                    <Exclamation height={32} width={32} />
                  </div>
                  <div className='journey-title'>WRPO NUMBER</div>
                  <div>
                    Complete Return PO step first!
                  </div>
                </div>}
                {row.wrpoNumber && <div className='journey-step success'>
                  <div className='journey-icon'>
                    <Check height={32} width={32} />
                  </div>
                  <div className='journey-title'>WRPO NUMBER</div>
                  <div className='journey-subtext'>
                    {row.wrpoNumber}
                  </div>
                </div>}
                {/* WRPO PDF */}
                <WootFbaJourneyStep 
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={false}
                  isSuccess={typeof row.sourceWrpoPdfPath === 'string'}
                  item={row.sourceWrpoPdfPath}
                  itemRequiresParse={false}
                  title='RETURN PDF'
                />
                {/* WRPO EXCEL UPLOAD */}
                <WootFbaJourneyStep 
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={false}
                  isSuccess={typeof row.sourceWrpoExcelPath === 'string'}
                  item={row.sourceWrpoExcelPath}
                  itemRequiresParse={false}
                  title='RETURN EXCEL'
                />
                <WootFbaJourneyStep 
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={true}
                  isSuccess={typeof row.returnIagResponsePath === 'string'}
                  item={row.returnIagResponsePath}
                  itemRequiresParse={false}
                  title='CREATED RETURN IA GROUPS'
                />
                <WootFbaJourneyStep 
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={true}
                  isSuccess={typeof row.returnOrderResponsePath === 'string'}
                  item={row.returnOrderResponsePath}
                  itemRequiresParse={false}
                  title='CREATED RETURN ORDER'
                />
              </div>
              <span className='beacon-header'>RESULTS</span>
              <div style={{display: 'flex', flexWrap: 'wrap'}}>
                {/* ALLOCATION ITEMS */}
                <WootFbaJourneyStep
                  title='EMAIL SENT'
                  item={typeof row.invoiceEmailRow === 'string' ? 'COMPLETED' : 'INCOMPLETE'}
                  itemRequiresParse={false}
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={false}
                  isSuccess={typeof row.invoiceEmailRow === 'string'}
                ></WootFbaJourneyStep>
              </div>
              <span className='beacon-header'>PREVIEW</span>
              {row.returnOrder && <WootFbaJourneyInvoicePreview row={row} />}
              
            </div>
          </div>
          <CustomError closeError={handleCloseInvoiceError} content={invoiceError?.content} description={invoiceError?.description} title={invoiceError?.title} visible={invoiceError?.visible} />
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
    </div>
  }, [props, sendTo])

  return view
}